import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { SOME_USERS_QUERY } from "@/pages/people/queries";
import type { MentionUser } from "@/components/AtMention/types";
import { splitMentionName } from "@/components/AtMention/mentionHelpers";

export default function useMentionSearch(query: string) {
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    if (!query) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, loading } = useQuery(SOME_USERS_QUERY, {
    variables: { search: debouncedQuery },
    skip: !debouncedQuery
  });

  const users: MentionUser[] = useMemo(() => {
    const raw: Array<{ id: string; name: string; email: string; photo?: string | null }> = data?.backofficeUsers ?? [];
    return raw.map((u) => {
      const { firstName, lastName } = splitMentionName(u.name);
      return { id: u.id, firstName, lastName, email: u.email, photo: u.photo ?? undefined };
    });
  }, [data]);

  return { users, isLoading: loading && !!debouncedQuery };
}
